import Phaser from 'phaser';
import { COLORS, GAME_W, GAME_H, intToHex } from '../utils/constants';
import { authManager } from '../systems/AuthManager';
import { soundManager } from '../systems/SoundManager';
import { showInput, hideAllInputs } from '../utils/inputs';

export default class LoginScene extends Phaser.Scene {
  private mode: 'login' | 'register' = 'login';
  private titleText!: Phaser.GameObjects.Text;
  private usernameDisplay!: Phaser.GameObjects.Text;
  private passwordDisplay!: Phaser.GameObjects.Text;
  private confirmDisplay!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private actionBtn!: Phaser.GameObjects.Container;
  private switchBtn!: Phaser.GameObjects.Container;
  private confirmLabel!: Phaser.GameObjects.Text;
  private confirmBg!: Phaser.GameObjects.Rectangle;
  private username = '';
  private password = '';
  private confirm = '';

  constructor() { super({ key: 'LoginScene' }); }

  create() {
    this.cameras.main.setBackgroundColor(COLORS.bgDark);

    // Grid background
    const grid = this.add.graphics().setDepth(0);
    grid.lineStyle(1, COLORS.cyan, 0.03);
    for (let x = 0; x < GAME_W; x += 40) grid.lineBetween(x, 0, x, GAME_H);
    for (let y = 0; y < GAME_H; y += 40) grid.lineBetween(0, y, GAME_W, y);

    this.add.text(GAME_W / 2, 60, '神经决斗', {
      fontFamily: 'Microsoft YaHei, Arial, sans-serif',
      fontSize: '36px', color: intToHex(COLORS.cyan), fontStyle: 'bold',
    }).setOrigin(0.5);

    this.titleText = this.add.text(GAME_W / 2, 120, '登录', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '24px', color: intToHex(COLORS.white),
    }).setOrigin(0.5);

    // Username
    this.add.text(GAME_W / 2, 185, '用户名', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '14px', color: COLORS.textDim,
    }).setOrigin(0.5);

    const uBg = this.add.rectangle(GAME_W / 2, 215, 320, 40, COLORS.bgPanel, 0.8)
      .setStrokeStyle(2, COLORS.cyan, 0.5);

    this.usernameDisplay = this.add.text(GAME_W / 2, 215, '点击输入', {
      fontFamily: 'Arial, sans-serif', fontSize: '20px', color: COLORS.textDim,
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    this.usernameDisplay.on('pointerdown', () => {
      showInput('nd-input-user', GAME_W / 2, 215, (val) => {
        this.username = val;
        this.usernameDisplay.setText(val || '点击输入');
        this.usernameDisplay.setColor(val ? COLORS.text : COLORS.textDim);
      });
    });

    // Password
    this.add.text(GAME_W / 2, 270, '密码', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '14px', color: COLORS.textDim,
    }).setOrigin(0.5);

    const pBg = this.add.rectangle(GAME_W / 2, 300, 320, 40, COLORS.bgPanel, 0.8)
      .setStrokeStyle(2, COLORS.cyan, 0.5);

    this.passwordDisplay = this.add.text(GAME_W / 2, 300, '点击输入', {
      fontFamily: 'Arial, sans-serif', fontSize: '20px', color: COLORS.textDim,
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    this.passwordDisplay.on('pointerdown', () => {
      showInput('nd-input-pass', GAME_W / 2, 300, (val) => {
        this.password = val;
        this.passwordDisplay.setText(val ? '*'.repeat(val.length) : '点击输入');
        this.passwordDisplay.setColor(val ? COLORS.text : COLORS.textDim);
      });
    });

    // Confirm password (hidden in login mode)
    this.confirmLabel = this.add.text(GAME_W / 2, 355, '确认密码', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '14px', color: COLORS.textDim,
    }).setOrigin(0.5).setVisible(false);

    this.confirmBg = this.add.rectangle(GAME_W / 2, 385, 320, 40, COLORS.bgPanel, 0.8)
      .setStrokeStyle(2, COLORS.cyan, 0.5).setVisible(false);

    this.confirmDisplay = this.add.text(GAME_W / 2, 385, '再次输入', {
      fontFamily: 'Arial, sans-serif', fontSize: '20px', color: COLORS.textDim,
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setVisible(false);

    this.confirmDisplay.on('pointerdown', () => {
      showInput('nd-input-confirm', GAME_W / 2, 385, (val) => {
        this.confirm = val;
        this.confirmDisplay.setText(val ? '*'.repeat(val.length) : '再次输入');
        this.confirmDisplay.setColor(val ? COLORS.text : COLORS.textDim);
      });
    });

    // Status
    this.statusText = this.add.text(GAME_W / 2, 425, '', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '14px', color: '#ff1744',
    }).setOrigin(0.5);

    // Action button
    this.actionBtn = this.makeBtn(GAME_W / 2, 465, '登录', COLORS.cyan, () => this.submit());

    // Switch mode
    this.switchBtn = this.makeBtn(GAME_W / 2, 520, '没有账号？注册', COLORS.purple, () => {
      this.mode = this.mode === 'login' ? 'register' : 'login';
      this.titleText.setText(this.mode === 'login' ? '登录' : '注册');
      (this.actionBtn.list[2] as Phaser.GameObjects.Text).setText(this.mode === 'login' ? '登录' : '注册');
      (this.switchBtn.list[2] as Phaser.GameObjects.Text).setText(
        this.mode === 'login' ? '没有账号？注册' : '已有账号？登录'
      );
      const show = this.mode === 'register';
      this.confirmLabel.setVisible(show);
      this.confirmBg.setVisible(show);
      this.confirmDisplay.setVisible(show);
      this.statusText.setText('');
    });

    // Back
    const backBtn = this.add.text(80, GAME_H - 50, '← 返回', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '18px', color: COLORS.textDim,
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => {
      hideAllInputs();
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.time.delayedCall(300, () => this.scene.start('TitleScene'));
    });

    this.events.on('shutdown', hideAllInputs);
  }

  private async submit() {
    hideAllInputs();
    if (!this.username || !this.password) {
      this.statusText.setText('请填写用户名和密码').setColor('#ff1744');
      return;
    }
    if (this.mode === 'register') {
      if (!this.confirm) { this.statusText.setText('请确认密码').setColor('#ff1744'); return; }
      if (this.password !== this.confirm) { this.statusText.setText('两次密码不一致').setColor('#ff1744'); return; }
    }

    this.statusText.setText('处理中...').setColor('#ffea00');
    const result = this.mode === 'login'
      ? await authManager.login(this.username, this.password)
      : await authManager.register(this.username, this.password);

    if (result.success) {
      this.statusText.setText('成功！').setColor('#00e676');
      soundManager.play('challenge_success');
      hideAllInputs();
      this.cameras.main.fadeOut(500, 0, 0, 30);
      this.time.delayedCall(500, () => this.scene.start('CameraSetupScene'));
    } else {
      this.statusText.setText(result.error || '操作失败').setColor('#ff1744');
      soundManager.play('challenge_fail');
    }
  }

  private makeBtn(x: number, y: number, text: string, color: number, cb: () => void): Phaser.GameObjects.Container {
    const w = 320, h = 48;
    const btn = this.add.container(x, y, [
      this.add.rectangle(0, 0, w, h, color).setOrigin(0.5).setAlpha(0.2),
      this.add.rectangle(0, 0, w, h, 0x000000).setOrigin(0.5).setStrokeStyle(2, color),
      this.add.text(0, 0, text, {
        fontFamily: 'Arial, sans-serif', fontSize: '20px', color: intToHex(color), fontStyle: 'bold',
      }).setOrigin(0.5),
    ]);
    btn.setSize(w, h).setInteractive({ useHandCursor: true });
    btn.on('pointerover', () => (btn.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.4));
    btn.on('pointerout', () => (btn.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.2));
    btn.on('pointerdown', () => { soundManager.play('ui_click'); cb(); });
    return btn;
  }
}
